import Big from "big.js";

import { CostType, Stage } from "./CaseConfiguration.ts";
import { NamedEntity } from "./CaseModel.ts";

export interface Budget {
  partyId: string;
  costs: Costs[];
}

/** A party's costs of one stage. */
export interface Costs extends NamedEntity {
  stage: Stage;
  flatFees: CostItem[];
}

/** What of an amount is incurred, and what is still estimated. */
export interface CostPart {
  incurred: number;
  estimated: number;
}

/**
 * A cost of one stage and type, e.g. an invoice: its part towards each
 * opponent and its generic part, owed by every opponent the party wins
 * against. Its amount is the sum of its parts.
 */
export interface CostItem {
  readonly id: string;
  type: CostType;
  note?: string;
  towards: { [opponentId: string]: CostPart };
  generic: CostPart;
}

/** What accepted settlements locked of a party's costs of one stage and type towards one target. */
export interface LockedCost {
  readonly stage: Stage;
  readonly type: CostType;
  /** The opponent the costs are towards; absent, they are generic. */
  readonly opponentId?: string;
  readonly incurred: number;
  readonly estimated: number;
  /** The accepted offer that locked them. */
  readonly offerId: string;
}

/** What accepted settlements locked of a party's costs, as the server computes it. */
export interface LockedCosts {
  /**
   * The opponents whose case with the party an accepted offer ended, each
   * with that offer: also those the party has no costs towards.
   */
  readonly ended: { readonly [opponentId: string]: string };
  /**
   * Where that is all its opponents, the last of those offers: with it the
   * party left the case, and all its costs are locked.
   */
  readonly exit?: string;
  readonly costs: readonly LockedCost[];
}

export class OptionalBig {
  private value: Big | undefined = undefined;

  addBig(another: Big): OptionalBig {
    this.value = this.value !== undefined ? this.value.add(another) : another;
    return this;
  }

  mulBig(another: Big): OptionalBig {
    if (this.value !== undefined) this.value = this.value.mul(another);
    return this;
  }

  mulNumber(another: number): OptionalBig {
    if (this.value !== undefined) this.value = this.value.mul(another);
    return this;
  }

  addNumber(another: number): OptionalBig {
    this.value =
      this.value !== undefined ? this.value.add(another) : new Big(another);
    return this;
  }

  add(another: OptionalBig): OptionalBig {
    if (another.value === undefined) return this;
    this.value =
      this.value !== undefined ? this.value.add(another.value) : another.value;
    return this;
  }

  toNumber = () => this.value?.toNumber();

  or = (alternative: number) => this.toNumber() || alternative;
}
